import { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, DatePicker, Tag, Button, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useFetchRFIDTransactions } from './Hook';
import { ReloadOutlined } from '@ant-design/icons';
import useDebounce from '../../utils/debounce';
import WarningMessage from '../../components/WarningMessage';
import {
  DebouncedSelect,
  fetchChargerList,
  fetchCustomerList,
  fetchLocationList,
  fetchRfidSelect,
} from '../../components/useDebouncedSelectLocation';
import CustomerDrawerView from '../../components/CustomerDrawerView';
import LocationSingleDrawer from '../locations/[id]/LocationSingleDrawer';
import SingleChargerDrawer from '../locations/[id]/SingleChargerDrawer';
import ViewRFIDModal from '../../components/Modals/ViewRFIDModal';
import { unixToLocalDate, formatKwFromWatts } from '../../utils/CommonFunctions';

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_COLORS = {
    PENDING: 'gold',
    COMPLETED: 'green',
    FAILED: 'red',
};

const TYPE_COLORS = {
    CREDIT: 'blue',
    DEBIT: 'volcano',
};

const SOURCE_COLORS = {
    INITIAL_LOAD: 'cyan',
    TOP_UP: 'geekblue',
    USAGE: 'purple',
};

const SEARCH_TYPE_OPTIONS = [
    { labelKey: 'RFID Tag', value: 'RFID' },
    { labelKey: 'Customer', value: 'CUSTOMER' },
    { labelKey: 'Location', value: 'LOCATION' },
    { labelKey: 'Charger', value: 'CHARGER' },
];

const RFIDTransactionList = () => {
    const { t } = useTranslation('rfid');
    const { operatorId } = useOperatorStore();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchType, setSearchType] = useState('RFID');
    const [searchQuery, setSearchQuery] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [status, setStatus] = useState(undefined);
    const [type, setType] = useState(undefined);
    const [source, setSource] = useState(undefined);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [openCustomerModal, setOpenCustomerModal] = useState(null);
    const [openLocationModal, setOpenLocationModal] = useState(null);
    const [openChargerModal, setOpenChargerModal] = useState(null);
    const [rfidToView, setRfidToView] = useState(null);

    const searchValue = searchQuery && typeof searchQuery === 'object' ? searchQuery.value : searchQuery;
    const debouncedSearch = useDebounce(searchValue, 500);

    useEffect(() => {
        setSearchQuery(null);
    }, [searchType]);

    const offset = page;

    const payload = useMemo(() => {
        return {
            operatorId,
            offset,
            limit: pageSize,
            ...(debouncedSearch && searchType === 'RFID' && { rfidId: debouncedSearch }),
            ...(debouncedSearch && searchType !== 'RFID' && { search: debouncedSearch }),
            ...(status && { status }),
            ...(type && { type }),
            ...(source && { source }),
            ...(dateRange && {
                fromDate: dateRange[0].startOf('day').toISOString(),
                toDate: dateRange[1].endOf('day').toISOString(),
            }),
        };
    }, [operatorId, offset, pageSize, debouncedSearch, searchType, status, type, source, dateRange]);

    const { data, isFetching, refetch } = useFetchRFIDTransactions(payload, {
        enabled: !!operatorId,
    });

    const transactions = data?.paginatedData || [];
    const totalCount = data?.totalCount || 0;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, dateRange, status, type, source, operatorId]);

    useEffect(() => {
        setFiltersApplied(!!(searchValue || status || type || source || (dateRange && dateRange.length)));
    }, [searchValue, status, type, source, dateRange]);

    const searchOptions = useMemo(
        () => SEARCH_TYPE_OPTIONS.map((opt) => ({ label: t(opt.labelKey), value: opt.value })),
        [t]
    );

    const handleClearFilters = useCallback(() => {
        setSearchQuery(null);
        setStatus(undefined);
        setType(undefined);
        setSource(undefined);
        setDateRange(null);
        setPage(1);
        setFiltersApplied(false);
    }, []);

    const renderSearchInput = () => {
        let fetchOptions;
        if (searchType === 'RFID') {
            fetchOptions = (search) => fetchRfidSelect({ operatorId, search });
        } else if (searchType === 'LOCATION') {
            fetchOptions = (search) => fetchLocationList({ operatorId, search });
        } else if (searchType === 'CHARGER') {
            fetchOptions = (search) => fetchChargerList({ operatorId, search });
        } else {
            fetchOptions = (search) => fetchCustomerList({ operatorId, search });
        }
        return (
            <DebouncedSelect
                value={searchQuery}
                onChange={setSearchQuery}
                operatorId={operatorId}
                allowClear
                fetchOptions={fetchOptions}
                placeholder={searchType === 'RFID' ? t('Search RFID...') : t('typeForSearch')}
                className="w-full md:!max-w-[250px]"
                labelInValue={searchType === 'CHARGER'}
            />
        );
    };

    const columns = [
        {
            title: t('Date'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date) => date ? unixToLocalDate(date) : '-',
        },
        {
            title: t('RFID Tag'),
            key: 'rfidTag',
            render: (_, record) => {
                const rfid = record?.rfidDetails?.[0] ?? (record?.rfidId && (typeof record.rfidId === 'object' ? record.rfidId : { _id: record.rfidId, RFID: record.rfidId }));
                const text = rfid?.RFID ?? record?.rfidId ?? '-';
                if (!rfid?._id) return text;
                return (
                    <span
                        className="cursor-pointer text-primary hover:underline"
                        onClick={() => setRfidToView(rfid)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setRfidToView(rfid)}
                    >
                        {text}
                    </span>
                );
            },
        },
        {
            title: t('Customer'),
            key: 'customer',
            render: (_, record) => {
                const c = record?.customerDetails?.[0];
                if (!c) return '-';
                const name = `${c.fname || ''} ${c.lname || ''}`.trim();
                const text = name || c.phoneNumber || '-';
                const customerId = c._id ?? c.id ?? record?.customerId;
                const rowOperatorId = record?.operatorId ?? operatorId;
                return (
                    <span
                        className="cursor-pointer text-primary hover:underline"
                        onClick={() => {
                            if (!customerId || !rowOperatorId) return;
                            setOpenCustomerModal({ id: customerId, operatorId: rowOperatorId });
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && customerId && rowOperatorId) {
                                setOpenCustomerModal({ id: customerId, operatorId: rowOperatorId });
                            }
                        }}
                    >
                        {text}
                    </span>
                );
            },
        },
        {
            title: t('Position'),
            key: 'position',
            align: 'center',
            render: (_, record) => record?.customerDetails?.[0]?.position || '—',
        },
        {
            title: t('Location'),
            key: 'location',
            render: (_, record) => {
                const loc = record?.locationDetails?.[0];
                const text = loc?.locationName ?? record?.locationId?.locationName ?? record?.locationId?.name ?? '-';
                if (!loc?._id) return text;
                return (
                    <span
                        className="cursor-pointer text-primary hover:underline text-xs"
                        onClick={() => setOpenLocationModal(loc)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setOpenLocationModal(loc)}
                    >
                        {text}
                    </span>
                );
            },
        },
        {
            title: t('Charger'),
            key: 'charger',
            render: (_, record) => {
                const ch = record?.chargerDetails?.[0];
                const text = ch?.name ?? record?.chargerId?.name ?? record?.chargerId?.chargerId ?? '-';
                if (!ch?._id) return text;
                return (
                    <span
                        className="cursor-pointer text-primary hover:underline"
                        onClick={() => setOpenChargerModal({ id: ch._id, operatorId: record?.operatorId || operatorId })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setOpenChargerModal({ id: ch._id, operatorId: record?.operatorId || operatorId })}
                    >
                        {text}
                    </span>
                );
            },
        },
        {
            title: t('Source'),
            dataIndex: 'source',
            key: 'source',
            render: (source) => (
                <Tag color={SOURCE_COLORS[source] || 'default'}>{(source || '').replace(/_/g, ' ')}</Tag>
            ),
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag variant="filled" color={TYPE_COLORS[type] || 'default'}>{type}</Tag>
            ),
        },
        // {
        //   title: t('Usage'),
        {
            title: t('Usage'),
            dataIndex: 'usage',
            key: 'usage',
            render: (val) => {
                if (val == null) return '-';
                return `${formatKwFromWatts(val)} kW`;
            },
        },
        
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag variant="outlined" color={STATUS_COLORS[status] || 'default'}>{t(status, { defaultValue: status })}</Tag>
            ),
        },
        {
            title: t('Reason'),
            dataIndex: 'reason',
            key: 'reason',
            render: (text) => <Tooltip title={text}>{text || '-'}</Tooltip>,
            ellipsis: {
                showTitle: false,
            },
        },
    ];

    if (!operatorId) return <WarningMessage />;

    return (
        <div className="h-full flex flex-col gap-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center flex-1">
                    <Select
                        value={searchType}
                        onChange={setSearchType}
                        className="w-full md:!max-w-[170px]"
                        options={searchOptions}
                    />
                    {renderSearchInput()}
                    <Select
                        placeholder={t('Select Status')}
                        className="w-full md:!max-w-[140px]"
                        allowClear
                        value={status}
                        onChange={(v) => { setStatus(v); setPage(1); }}
                    >
                        <Option value="PENDING">{t('PENDING')}</Option>
                        <Option value="COMPLETED">{t('COMPLETED')}</Option>
                        <Option value="FAILED">{t('FAILED')}</Option>
                    </Select>
                    <Select
                        placeholder={t('Select Type')}
                        className="w-full md:!max-w-[120px]"
                        allowClear
                        value={type}
                        onChange={(v) => { setType(v); setPage(1); }}
                    >
                        <Option value="CREDIT">{t('CREDIT')}</Option>
                        <Option value="DEBIT">{t('DEBIT')}</Option>
                    </Select>
                    <Select
                        placeholder={t('Select Source')}
                        className="w-full md:!max-w-[120px]"
                        allowClear
                        value={source}
                        onChange={(v) => { setSource(v); setPage(1); }}
                    >
                        <Option value="INITIAL_LOAD">{t('INITIAL_LOAD')}</Option>
                        <Option value="TOP_UP">{t('TOP_UP')}</Option>
                        <Option value="USAGE">{t('USAGE')}</Option>
                    </Select>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => { setDateRange(dates); setPage(1); }}
                        className="w-full md:!max-w-[250px]"
                    />
                    {filtersApplied && (
                        <Button onClick={handleClearFilters}>{t('clear')}</Button>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={refetch} />
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={transactions}
                loading={isFetching}
                rowKey={(record) => record._id}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    position: ['bottomCenter'],
                    onChange: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                    }
                }}
                scroll={{ x: 1200 }}
            />

            {openCustomerModal?.id && (
                <CustomerDrawerView
                    id={openCustomerModal.id}
                    operatorId={openCustomerModal.operatorId}
                    open={Boolean(openCustomerModal?.id)}
                    onClose={() => setOpenCustomerModal(null)}
                />
            )}
            {openLocationModal && (
                <LocationSingleDrawer
                    isOpen={Boolean(openLocationModal)}
                    onClose={() => setOpenLocationModal(null)}
                    selectedLocation={openLocationModal}
                    OPId={operatorId}
                />
            )}
            {openChargerModal && (
                <SingleChargerDrawer
                    selectedCharger={openChargerModal?.id || ''}
                    isOpen={Boolean(openChargerModal)}
                    onClose={() => setOpenChargerModal(null)}
                    passoperatorId={openChargerModal?.operatorId || operatorId}
                />
            )}
            {rfidToView && (
                <ViewRFIDModal
                    chargerData={rfidToView}
                    open={Boolean(rfidToView)}
                    setOpen={() => setRfidToView(null)}
                    refetchRFIDList={refetch}
                />
            )}
        </div>
    );
};

export default RFIDTransactionList;
